import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useShallowAppSelector } from 'store/hooks';
import { AppActions, DashboardActions, FileActions } from 'store/actionCreators';
import { color } from 'styles/utils';
import CloudStorageData from 'components/util/CloudStorageData';
import CloudProjectList from 'components/util/CloudProjectList';
import CloudSimpleRemove from 'components/util/CloudSimpleRemove';
import CustomSpan from 'components/common/text/CustomSpan';
import { T } from 'components/common/text';
import { IconButton } from '@material-ui/core';
import TrashIcon from 'components/base/icons/TrashIcon';
import DownloadIcon from 'components/base/icons/DownloadIcon';
import useInput from 'lib/hooks/useInput';
import CloudProjectSearch from 'components/util/CloudProjectSearch';
import useDateInput from 'lib/hooks/useDateInput';
import { IFetchCloudeProject } from 'lib/types/utilTypes';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import { useDidUpdateEffect } from 'lib/utils';
import { debounce } from 'lodash';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import { useTranslation } from 'react-i18next';
import { useCloudManageStore } from 'store/local/cloudManage';
import ClinicModal from 'components/common/modal/ClinicModal';
import CloudDownloadModal from 'components/util/CloudDownloadModal';

export type TCloudChart = {
  title: string;
  value: number;
  color: string;
};

export type TCloudProject = {
  projectId: string;
  projectIdx: number;
  projectCode: string;
  projectName: string;
  patientLastName: string | null;
  patientFirstName: string | null;
  identification: string | null;
  totalSize: number;
  totalSizeMB: number;
  modifyDate: number;
  isChecked?: boolean;
};

export type TDeletePeriod = '15' | '30' | '90';

export interface IDownloadSubmit {
  projectInfoList: TProjectInfo[];
  downloadPath: string;
  totalFileBytes: number;
  maxFileBytes: number;
}

export type TProjectInfo = {
  projectIdx: number;
  projectId: string;
};

interface CloudManagementModalContainerProps {
  onCloseModal: () => void;
}

function CloudManagementModalContainer({ onCloseModal }: CloudManagementModalContainerProps) {
  const {
    productData: {
      dataInfo: { productName, totalSize, totalUsage },
    },
    cloudProject: { list: cloudProjectListData, pagingData: cloudProjectListPagingData },
    cloudProjectSuccess,
    cloudProjectFailure,
    deleteCloudSuccess,
    deleteCloudPeriodSuccess,
  }: {
    productData: {
      dataInfo: {
        isExpired: number;
        productName: string;
        totalSize: number;
        totalUsage: number;
      };
    };
    cloudProject: {
      list: TCloudProject[];
      pagingData: OffsetPagingData;
    };
    cloudProjectSuccess: boolean;
    cloudProjectFailure: boolean;
    deleteCloudSuccess: boolean;
    deleteCloudPeriodSuccess: boolean;
  } = useShallowAppSelector(state => ({
    productData: state.dashboard.productData.data || {
      dataInfo: {
        isExpired: 0,
        productName: 'Basic',
        totalSize: 5,
        totalUsage: 0,
      },
    },
    cloudProject: state.file.cloudProjectList.data || {},
    cloudProjectSuccess: state.file.cloudProjectList.success,
    cloudProjectFailure: state.file.cloudProjectList.failuer,
    deleteCloudSuccess: state.file.deleteCloudProjects.success,
    deleteCloudPeriodSuccess: state.file.deleteCloudProjectsPeriod.success,
  }));
  const DOWNLOADSPACE = 'DOWNLOADSPACE';
  const limitCount = 20;
  const { ipcRenderer } = window;
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isLoadingSuccess, setIsLoadingSuccess] = useState<boolean>(false);
  const [isLoadingFailure, setIsLoadingFailure] = useState<boolean>(false);

  const hasMoreProjectList =
    cloudProjectListPagingData?.page < cloudProjectListPagingData?.totalPage;

  const syncItemName = productName; // productName
  const syncItemUseVolume = Number(totalUsage); // totalUsage
  const syncItemVolume = Number(totalSize); // totalSize

  const syncChartItems: TCloudChart[] = [
    { title: 'available', value: syncItemVolume - syncItemUseVolume, color: '#F4F5FA' },
    { title: syncItemName, value: syncItemUseVolume, color: color.blue },
  ];

  const period = useDateInput(null);
  const keyword = useInput('');
  const periodType = useInput('all');
  const sortType = useInput('oldest');
  const [cloudProjectList, setCloudProjectList] = useState<TCloudProject[]>([]);
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const checkedList = useCheckSetInput(new Set([]));
  const [isListOver, setIsListOver] = useState<boolean>(false);
  const [downloadPath, setDownloadPath] = useState<string>('');
  const { cloudManageOpen, setCloudManageOpen } = useCloudManageStore();
  const [downloadData, setDownloadData] = useState<IDownloadSubmit | null>(null);

  const searchParamsDatas: IFetchCloudeProject = useMemo(() => {
    return {
      sort: sortType.value,
      period: periodType.value,
      startDate: periodType.value === 'user' && !!period.value ? period.value[0].unix() : null,
      endDate: periodType.value === 'user' && !!period.value ? period.value[1].unix() : null,
      keyword: keyword.value,
      page: 1,
      limitCount: limitCount,
    };
  }, [sortType.value, periodType.value, keyword.value, period.value]);

  const handleCheckAllProjects = (checkAll: boolean) => {
    // Check All click 시 일단 초기화
    checkedList.setValue(new Set([]));
    setCheckAll(!checkAll);
    if (!checkAll) {
      cloudProjectList.forEach((item: TCloudProject) => {
        checkedList.onChange({ value: item });
      });
    }
  };

  const handleCheckProject = (project: TCloudProject) => {
    checkedList.onChange({ value: project });
  };

  const handleClickLocalDownload = () => {
    if (ipcRenderer) {
      if ([...checkedList.value].length > 20) {
        setIsListOver(true);
        setIsDownloading(true);
      } else {
        ipcRenderer.send('openDirectory', { directoryType: DOWNLOADSPACE });
        setIsListOver(false);
      }
    }
  };

  const handleContinueLocalDownload = () => {
    if (!ipcRenderer) return;
    if ([...checkedList.value].length === 0) return;
    let listTotal = 0;
    const projectInfoList = [...checkedList.value].map((item: TCloudProject) => {
      listTotal = listTotal + item.totalSize;
      return { projectIdx: item.projectIdx, projectId: item.projectId };
    });
    const largestSizeProject = [...checkedList.value].reduce((max, curr) => {
      return curr.totalSize > max.totalSize ? curr : max;
    });

    const submit = {
      projectInfoList: projectInfoList,
      downloadPath,
      totalFileBytes: listTotal,
      maxFileBytes: largestSizeProject.totalSize,
    };
    setDownloadData(submit);

    console.log('HDS] handleContinueLocalDownload __  submit _______ ', submit);

    // ipcRenderer.send('multipleProjectFileDownloadToLocal', submit);
    checkedList.setValue(new Set([]));
    // IPC요청 후, downloadPath 초기화
    setIsDownloading(true);
    setDownloadPath('');
  };

  const handleClikcPeriodDelete = (type: TDeletePeriod) => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 570,
      title: '',
      content: (
        <AlertModalContent
          title={t('REMOVE_PROJECT_FILES')}
          mainText={t('REMOVE_PERIOD_PROJECT_FILE', { period: type })}
        />
      ),
      onClick() {
        FileActions.delete_cloud_projects_period_request({
          days: type,
        });
        return;
      },
    });
  };

  const handleClickDelete = () => {
    if ([...checkedList.value].length === 0) return;
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 570,
      title: '',
      content: (
        <AlertModalContent
          title={t('REMOVE_PROJECT_FILES')}
          mainText={t('REMOVE_PROJECT_FILES_DESCRIPT', { count: [...checkedList.value].length })}
        />
      ),
      onClick() {
        const projectIdxList = [...checkedList.value].map((item: TCloudProject) => {
          return item.projectIdx;
        });

        console.log('HDS] handleClickDelete _____ projectIdxList _____ ', projectIdxList);
        FileActions.delete_cloud_projects_request({
          projectIdxList: projectIdxList,
        });
        return;
      },
    });
  };

  const handleSearchProject = () => {
    setCloudProjectList([]);
    FileActions.fetch_cloud_project_list_request(searchParamsDatas);
    setIsLoadingSuccess(false);
    setIsLoadingFailure(false);
  };

  const handleClaerKeyword = () => {
    keyword.setValue('');
  };

  const handleFetchCloudProjectList = debounce(() => {
    const { page } = cloudProjectListPagingData;
    if (!hasMoreProjectList) return;
    FileActions.fetch_cloud_project_list_request({ ...searchParamsDatas, page: page + 1 });
  }, 500);

  const handleRefreshList = () => {
    const submit = {
      sort: sortType.value,
      period: periodType.value,
      startDate: periodType.value === 'user' && !!period.value ? period.value[0].unix() : null,
      endDate: periodType.value === 'user' && !!period.value ? period.value[1].unix() : null,
      keyword: keyword.value,
      page: 1,
      limitCount: limitCount,
    };
    FileActions.fetch_cloud_project_list_request(submit);
    setIsLoadingSuccess(false);
    setIsLoadingFailure(false);
  };

  const handleCloseModal = () => {
    setIsDownloading(false);
  };

  const handleOpenWindow = async () => {
    if (!ipcRenderer) return;

    try {
      const openWindow = await ipcRenderer.invoke('openWindow', {
        directoryType: 'DOWNLOADSPACE',
      });
      console.log('[kkw] openWidow _______', openWindow);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!ipcRenderer) return;
    ipcRenderer.on(
      'openDirectoryRes',
      (e: any, data: { directoryType: string; canceled: boolean; filePaths: string[] }) => {
        if (!!data.canceled) return;
        if (data.directoryType === DOWNLOADSPACE) {
          setDownloadPath(data.filePaths[0]);
        }
      },
    );
  }, []);

  useEffect(() => {
    if (!downloadPath) return;
    handleContinueLocalDownload();
  }, [downloadPath]);

  useEffect(() => {
    if (cloudProjectList.length > 0 && checkedList.value.size === cloudProjectList.length) {
      setCheckAll(true);
    } else {
      setCheckAll(false);
    }
  }, [checkedList.value, cloudProjectList]);

  useEffect(() => {
    if (cloudManageOpen) setCloudManageOpen(false);

    FileActions.fetch_cloud_project_list_request(searchParamsDatas);
    DashboardActions.fetch_product_data_request();
    return () => {
      setIsLoadingSuccess(false);
      setIsLoadingFailure(false);
      setDownloadPath('');
    };
  }, []);

  useDidUpdateEffect(() => {
    setCloudProjectList([]);
    setIsLoadingSuccess(false);
    setIsLoadingFailure(false);

    if (periodType.value === 'user') {
      if (!period.value) return;
      FileActions.fetch_cloud_project_list_request({
        sort: sortType.value,
        period: periodType.value,
        startDate: period.value[0].startOf('day').unix(),
        endDate: period.value[1].endOf('day').unix(),
        keyword: keyword.value,
        page: 1,
        limitCount: limitCount,
      });
    } else {
      FileActions.fetch_cloud_project_list_request({
        sort: sortType.value,
        period: periodType.value,
        startDate: null,
        endDate: null,
        keyword: keyword.value,
        page: 1,
        limitCount: limitCount,
      });
    }
  }, [sortType.value, periodType.value, period.value]);

  useDidUpdateEffect(() => {
    if (!cloudProjectSuccess) return;
    if (cloudProjectListPagingData.page === 1) {
      setCloudProjectList(cloudProjectListData);
      setIsLoadingSuccess(true);
    } else {
      setCloudProjectList(prev => [...prev, ...cloudProjectListData]);
    }
  }, [cloudProjectListData, cloudProjectSuccess, cloudProjectListPagingData]);

  useDidUpdateEffect(() => {
    if (!cloudProjectFailure) return;
    setIsLoadingFailure(true);
  }, [cloudProjectFailure]);

  useDidUpdateEffect(() => {
    if (!deleteCloudPeriodSuccess) return;
    checkedList.setValue(new Set([]));
    FileActions.fetch_cloud_project_list_request(searchParamsDatas);
    DashboardActions.fetch_product_data_request();
  }, [deleteCloudPeriodSuccess]);

  useDidUpdateEffect(() => {
    if (!deleteCloudSuccess) return;
    const projectIdxList = [...checkedList.value].map((item: TCloudProject) => {
      return item.projectIdx;
    });
    setCloudProjectList(prev => prev.filter(item => !projectIdxList.includes(item.projectIdx)));
    checkedList.setValue(new Set([]));
    DashboardActions.fetch_product_data_request();
  }, [deleteCloudSuccess, checkedList.value]);

  return (
    <StyledCloudManagementModal data-component-name="CloudManagementModal">
      <div className="cloudManagementModal__section left">
        <CloudStorageData chartData={syncChartItems} />
        <CloudSimpleRemove onClikcPeriodDelete={handleClikcPeriodDelete} />
      </div>
      <div className="cloudManagementModal__section right">
        <div className="cloudManagementModal__section_header">
          <div className="cloudManagementModal__section_title">
            <CustomSpan fontSize={26} fontWeight={600} fontColor="#444">
              <T>CLOUD_ALL_PROJECT</T>
            </CustomSpan>
            <CustomSpan fontSize={14} fontWeight={400} fontColor="#888">
              <T>CLOUD_MANAGE_DETAIL</T>
            </CustomSpan>
          </div>
          <div className="cloudManagementModal__header_botton_box">
            <IconButton
              className="cloudManagementModal__botton_box download"
              disabled={[...checkedList.value].length === 0}
              onClick={handleClickLocalDownload}
            >
              <div className="cloudManagementModal__botton">
                <DownloadIcon
                  width={14}
                  color={[...checkedList.value].length === 0 ? '#fff' : '#888'}
                />
                <CustomSpan
                  fontSize={14}
                  fontColor={[...checkedList.value].length === 0 ? '#fff' : '#888'}
                >
                  <T>GLOBAL_LOCAL_DOWNLOAD</T>
                </CustomSpan>
              </div>
            </IconButton>
            <IconButton
              className="cloudManagementModal__botton_box delete"
              disabled={[...checkedList.value].length === 0}
              onClick={handleClickDelete}
            >
              <div className="cloudManagementModal__botton">
                <TrashIcon width={14} color="#fff" />
                <CustomSpan fontSize={14} fontColor={'#fff'}>
                  <T>GLOBAL_DELETE</T>
                </CustomSpan>
              </div>
            </IconButton>
          </div>
        </div>
        <CloudProjectSearch
          keyword={keyword}
          periodType={periodType}
          sortType={sortType}
          period={period}
          onSearchProject={handleSearchProject}
          onClaerKeyword={handleClaerKeyword}
        />
        <CloudProjectList
          checkAll={checkAll}
          checkedList={checkedList.value}
          cloudProjectList={cloudProjectList}
          isLoadingSuccess={isLoadingSuccess}
          isLoadingFailure={isLoadingFailure}
          hasMoreFetchData={hasMoreProjectList}
          onFetchCloudProjectList={handleFetchCloudProjectList}
          onRefreshList={handleRefreshList}
          onCheckAllProjects={handleCheckAllProjects}
          onCheckProject={handleCheckProject}
        />
      </div>
      {useMemo(() => {
        return (
          <ClinicModal isOpen={isDownloading} isCloseIcon={false}>
            <CloudDownloadModal
              isListOver={isListOver}
              downloadData={downloadData}
              onCloseModal={handleCloseModal}
              onOpenWindow={handleOpenWindow}
            />
          </ClinicModal>
        );
      }, [isDownloading, isListOver])}
    </StyledCloudManagementModal>
  );
}
const StyledCloudManagementModal = styled.div<{}>`
  width: 100%;
  height: 800px;
  border-radius: 15px;
  background-color: #ffffff;
  position: relative;
  display: flex;
  overflow: hidden;
  .cloudManagementModal__section {
    display: flex;
    flex-direction: column;
    &.left {
      width: 340px;
    }
    &.right {
      width: calc(100% - 340px);
      padding: 30px 30px 0 30px;
      row-gap: 20px;
      border-left: 1px solid #e1e1e7;
    }
    .cloudManagementModal__section_header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      .cloudManagementModal__section_title {
        display: flex;
        flex-direction: column;
        row-gap: 8px;
      }
      .cloudManagementModal__header_botton_box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: 10px;
        .cloudManagementModal__botton_box {
          width: 156px;
          height: 38px;
          border-radius: 3px;
          &.download {
            border: 1px solid #e1e1e7;
          }
          &.delete {
            border: 1px solid ${color.blue};
            background-color: ${color.blue};
          }
          &:disabled {
            color: rgb(118, 118, 118, 0.5);
            background: rgba(118, 118, 118, 0.2);
            border-color: rgba(118, 118, 118, 0.1);
            cursor: initial;
          }
          .cloudManagementModal__botton {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            column-gap: 6px;
          }
        }
      }
    }
  }
`;
export default React.memo(CloudManagementModalContainer);
